import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";
import { LOGO } from "@/lib/assets";
import { LogOut, ChevronDown, Package, CalendarCheck, ShoppingBag, IndianRupee } from "lucide-react";

const ORDER_STATUS = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const CONSULT_STATUS = ["requested", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [consults, setConsults] = useState([]);
  const [products, setProducts] = useState([]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/consultations").then((r) => setConsults(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    if (user) loadAll();
  }, [user]);

  const updOrder = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    toast.success(`Order marked ${status}`);
    loadAll();
  };
  const updConsult = async (id, status) => {
    await api.patch(`/admin/consultations/${id}`, { status });
    toast.success(`Consultation marked ${status}`);
    loadAll();
  };

  if (!user) return <div className="min-h-screen bg-cream pt-40 text-center font-serif text-2xl">Loading…</div>;

  const cards = [
    { label: "Products", value: stats?.products ?? "—", icon: Package },
    { label: "Orders", value: stats?.orders ?? "—", icon: ShoppingBag },
    { label: "Consultations", value: stats?.consultations ?? "—", icon: CalendarCheck },
    { label: "Revenue", value: stats ? inr(stats.revenue) : "—", icon: IndianRupee },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <div className="border-b border-black/10 bg-cream/90 backdrop-blur sticky top-0 z-40">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="HairPrive" className="h-16 w-auto object-contain" />
            <div className="border-l border-black/15 pl-4">
              <p className="font-sans uppercase tracking-[0.3em] text-[10px] text-obsidian/50">Admin Dashboard</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-black/20 px-5 py-2.5 font-sans uppercase tracking-[0.2em] text-[11px] hover:bg-obsidian hover:text-cream transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-[1500px] px-5 md:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="bg-cream p-6" data-testid={`stat-${c.label.toLowerCase()}`}>
              <c.icon className="w-5 h-5 text-amber-deep" strokeWidth={1.5} />
              <p className="mt-4 font-serif text-4xl text-obsidian">{c.value}</p>
              <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-obsidian/50 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="bg-sand rounded-none p-1 h-auto">
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none font-sans uppercase tracking-[0.15em] text-[11px] data-[state=active]:bg-obsidian data-[state=active]:text-cream px-5 py-2.5">Orders</TabsTrigger>
            <TabsTrigger value="consultations" data-testid="tab-consultations" className="rounded-none font-sans uppercase tracking-[0.15em] text-[11px] data-[state=active]:bg-obsidian data-[state=active]:text-cream px-5 py-2.5">Consultations</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-none font-sans uppercase tracking-[0.15em] text-[11px] data-[state=active]:bg-obsidian data-[state=active]:text-cream px-5 py-2.5">Products</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-6">
            <div className="border border-black/10 bg-cream overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-black/10">
                    <TableHead>Customer</TableHead><TableHead>Contact</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} className="border-black/10" data-testid={`order-row-${o.id}`}>
                      <TableCell className="font-serif text-lg">{o.customer_name}<div className="font-sans text-xs text-obsidian/50">{o.city}, {o.pincode}</div></TableCell>
                      <TableCell className="font-sans text-sm">{o.email}<div className="text-obsidian/50">{o.phone}</div></TableCell>
                      <TableCell className="font-sans text-sm">{o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</TableCell>
                      <TableCell className="font-serif">{inr(o.total)}</TableCell>
                      <TableCell><StatusMenu value={o.status} options={ORDER_STATUS} onChange={(s) => updOrder(o.id, s)} /></TableCell>
                    </TableRow>
                  ))}
                  {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center font-sans text-obsidian/50 py-10">No orders yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="consultations" className="mt-6">
            <div className="border border-black/10 bg-cream overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-black/10">
                    <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Date / Slot</TableHead><TableHead>Concern</TableHead><TableHead>Approach</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consults.map((c) => (
                    <TableRow key={c.id} className="border-black/10" data-testid={`consult-row-${c.id}`}>
                      <TableCell className="font-serif text-lg">{c.name}</TableCell>
                      <TableCell className="font-sans text-sm">{c.email}<div className="text-obsidian/50">{c.phone}</div></TableCell>
                      <TableCell className="font-sans text-sm">{c.date}<div className="text-obsidian/50">{c.time_slot}</div></TableCell>
                      <TableCell className="font-sans text-sm">{c.concern}</TableCell>
                      <TableCell className="font-sans text-sm">{c.system_preference}</TableCell>
                      <TableCell><StatusMenu value={c.status} options={CONSULT_STATUS} onChange={(s) => updConsult(c.id, s)} /></TableCell>
                    </TableRow>
                  ))}
                  {consults.length === 0 && <TableRow><TableCell colSpan={6} className="text-center font-sans text-obsidian/50 py-10">No consultations yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <div className="border border-black/10 bg-cream overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-black/10"><TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead>System</TableHead><TableHead>Price</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.slug} className="border-black/10">
                      <TableCell className="flex items-center gap-3"><div className="w-10 h-12 bg-sand overflow-hidden"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div><span className="font-serif text-lg">{p.name}</span></TableCell>
                      <TableCell className="font-sans text-sm">{p.category}</TableCell>
                      <TableCell className="font-sans text-sm">{p.system}</TableCell>
                      <TableCell className="font-serif">{inr(p.price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const StatusMenu = ({ value, options, onChange }) => (
  <DropdownMenu>
    <DropdownMenuTrigger className="inline-flex items-center gap-1.5 border border-black/15 px-3 py-1.5 font-sans uppercase tracking-[0.15em] text-[10px] capitalize hover:border-obsidian transition-colors" data-testid="status-trigger">
      {value} <ChevronDown className="w-3 h-3" />
    </DropdownMenuTrigger>
    <DropdownMenuContent className="rounded-none">
      {options.map((o) => (
        <DropdownMenuItem key={o} onClick={() => onChange(o)} className="capitalize font-sans text-sm cursor-pointer">{o}</DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);
